import { ENDPOINTS } from "@api/endpoints";
import api, { handleError } from "@/api";
import type { UserDetails } from "@/types";
import type { GroupMember, GroupUserRole } from "@/types/groups";
import z from "zod";

const {
  GROUPS: {
    ADD_ADMINS_TO_GROUP,
    ADD_MEMBERS_TO_GROUP,
    CREATE_NEW_GROUP,
    GET_ALL_GROUPS,
    GET_GROUP_DETAILS_BY_ID,
    GET_USERS_BY_GROUP,
    UPDATE_GROUP_NAME,
    UPDATE_GROUP_ROLE,
    SORT_GROUPS,
    REMOVE_ADMINS_FROM_GROUP,
    REMOVE_MEMBERS_FROM_GROUP,
    DELETE_GROUP,
  },
  USER: { BASE: USER_BASE },
} = ENDPOINTS;

export type LegacyGroupUser = {
  members: GroupMember[];
  pendingInvites: {
    inviteeUserEmail: string;
    groupId: string | number;
    inviterUserId: number;
    status: string;
  }[];
};

export default {
  getAllGroups: async (signal?: AbortSignal) => {
    try {
      const response = await api<typeof GET_ALL_GROUPS.responseType>(
        GET_ALL_GROUPS.path,
        {
          method: GET_ALL_GROUPS.method,
          url: GET_ALL_GROUPS.path,
          signal,
        },
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  getGroupDetailsById: async (id: string, signal?: AbortSignal) => {
    try {
      const response = await api<typeof GET_GROUP_DETAILS_BY_ID.responseType>(
        GET_GROUP_DETAILS_BY_ID.path(id),
        { signal },
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  getUsersByGroup: async (groupId: string, signal?: AbortSignal) => {
    try {
      const allUsersUrl = `${USER_BASE}/all/group`;
      const getGroupMembersUrl =`${allUsersUrl}/${String(groupId)}`;
      const [getGroupMembersResult] = await Promise.allSettled([
        api<LegacyGroupUser>({
          method: GET_USERS_BY_GROUP.method,
          url: getGroupMembersUrl,
          signal,
        }),
      ]);
      if (getGroupMembersResult.status === "fulfilled") {
        return {
          status: true,
          data: getGroupMembersResult.value.data,
        };
      } else {
        const error =
          getGroupMembersResult.status === "rejected"
            ? getGroupMembersResult.reason
            : null;
        throw error ?? new Error("Failed to fetch group users");
      }
    } catch (error) {
      throw handleError(error);
    }
  },
  getAllUsersByGroup: async (signal?: AbortSignal) => {
    try {
      const allUsersUrl = `${USER_BASE}/all/group`;
      const response = await api<typeof GET_USERS_BY_GROUP.responseType>({
        method: GET_USERS_BY_GROUP.method,
        url: allUsersUrl,
        signal,
      });
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  createNewGroup: async (name: string) => {
    try {
      const { data, success } = z
        .object({
          name: z.string().min(1).trim(),
        })
        .safeParse({ name });
      if (!success) throw new Error("Invalid group name");
      const response = await api<typeof CREATE_NEW_GROUP.responseType>({
        method: CREATE_NEW_GROUP.method,
        url: CREATE_NEW_GROUP.path,
        data,
      });
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  addMembersToGroup: async (groupId: string, userIds: Array<number>) => {
    try {
      const response = await api<typeof ADD_MEMBERS_TO_GROUP.responseType>({
        method: ADD_MEMBERS_TO_GROUP.method,
        url: ADD_MEMBERS_TO_GROUP.path(groupId),
        data: { userIds },
      });
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  addAdminsToGroup: async (groupId: string, userIds: Array<number>) => {
    try {
      const response = await api<typeof ADD_ADMINS_TO_GROUP.responseType>({
        method: ADD_ADMINS_TO_GROUP.method,
        url: ADD_ADMINS_TO_GROUP.path(groupId),
        data: { userIds },
      });
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  removeAdminsFromGroup: async (groupId: string, userIds: Array<number>) => {
    try {
      const response = await api<typeof REMOVE_ADMINS_FROM_GROUP.responseType>({
        method: REMOVE_ADMINS_FROM_GROUP.method,
        url: REMOVE_ADMINS_FROM_GROUP.path(groupId),
        data: { userIds },
      });
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  removeMembersFromGroup: async (groupId: string, userIds: Array<number>) => {
    try {
      const response = await api<typeof REMOVE_MEMBERS_FROM_GROUP.responseType>(
        {
          method: REMOVE_MEMBERS_FROM_GROUP.method,
          url: REMOVE_MEMBERS_FROM_GROUP.path(groupId),
          data: { userIds },
        },
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  updateGroupName: async (groupId: string, name: string) => {
    try {
      const { data, success } = z
        .object({
          name: z.string().min(1).trim(),
        })
        .safeParse({ name });
      if (!success) throw new Error("Invalid group name");
      const response = await api<typeof UPDATE_GROUP_NAME.responseType>({
        method: UPDATE_GROUP_NAME.method,
        url: UPDATE_GROUP_NAME.path(groupId),
        data,
      });
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  sortGroups: async (sortGroupData: typeof SORT_GROUPS.requestType) => {
    try {
      const { data, success } = z
        .object({
          sortGroupData: z.array(
            z.object({
              groupId: z.string(),
              index: z.string(),
            }),
          ),
        })
        .safeParse(sortGroupData);
      if (!success) throw new Error("Invalid sort group data");
      const response = await api<typeof SORT_GROUPS.responseType>({
        method: SORT_GROUPS.method,
        url: SORT_GROUPS.path,
        data,
      });
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  /**
   * Change a group member's role (ADMIN, EDITOR, or READER).
   */
  changeGroupUserRole: async (
    groupId: string,
    payload: { userId: string | number; role: GroupUserRole },
  ) => {
    try {
      const response = await api<typeof UPDATE_GROUP_ROLE.responseType>({
        method: UPDATE_GROUP_ROLE.method,
        url: UPDATE_GROUP_ROLE.path(groupId),
        data: {
          userId: String(payload.userId),
          role: payload.role,
        },
      });
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  /**
   * Delete a group. Caller must be a group admin.
   */
  deleteGroup: async (groupId: string) => {
    try {
      const response = await api<typeof DELETE_GROUP.responseType>({
        method: DELETE_GROUP.method,
        url: DELETE_GROUP.path(groupId),
      });
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
};
